import { forEach, findIndex } from 'lodash';
import { createLogger } from 'shared/services/logger';
import { translateLayer } from 'shared/stores/annotations/legacy-translations';
import { store } from 'shared/stores/core/decorators';
import { makeObservable } from 'shared/stores/core/make-observable';
import { pull } from 'shared/util';
import { CURSOR_OPTIONS } from './consts';
import { translateRegion } from './legacy-translations';

const logger = createLogger('@store(ANNOTATIONS)');

@store('ANNOTATIONS')
export class AnnotationsStore {
  state = {
    stateByImageId: {}
  };

  getters = {
    currentState ({state}) {
      const imageId = state('IMAGE/selectedImageId');
      return this.getters.getState({imageId});
    },
    selectedLayer () {
      const state = this.getters.currentState;
      return state ? state.allLayers[state.selectedLayerId] || null : null;
    },
    selectedRegion () {
      const state = this.getters.currentState;
      return state ? state.allRegions[state.selectedRegionId] || null : null;
    },
    selectedRegionLayer () {
      const getters = this.getters;
      return getters.selectedRegion ? getters.getLayer(getters.selectedRegion) : null;
    },
    getState: () => ({imageId}) => {
      if (!imageId) {
        return null;
      }
      if (!this.state.stateByImageId[imageId]) {
        this.state.stateByImageId[imageId] = createState();
      }

      return this.state.stateByImageId[imageId];
    },
    getLayer: () => ({imageId, layerId}) => {
      return this.getters.getState({imageId}).allLayers[layerId];
    },
    getInputLayer: () => ({imageId, inputAnnotationId}) => {
      // Note that output layer *can* have null input layers. If the user deletes the IL in ImageScope, DataServer sets
      // inputAnnotationId to null.
      return inputAnnotationId ? this.getters.getState({imageId}).allLayers[inputAnnotationId] : null;
    },
    getOutputLayers: () => ({imageId, layerId, id}) => {
      id = layerId || id; // Regions have "layerId's" and "id's"; either can be used to get output layers
      if (!id) {
        logger.error('how is there no id?');
        return [];
      }
      const state = this.getters.getState({imageId});
      const layersArray = Object.values(state.allLayers);
      return layersArray.filter(l => l.inputAnnotationId === id);
    },
    getInputRegion: () => ({imageId, inputRegionId}) => {
      // Note that output layer *can* have null input layers. If the user deletes the IL in ImageScope, DataServer sets
      // inputRegionId to null.
      return inputRegionId ? this.getters.getState({imageId}).allRegions[inputRegionId] : null;
    },
    getOutputRegions: () => ({imageId, id}) => {
      const state = this.getters.getState({imageId});
      const regionsArray = Object.values(state.allRegions);
      return id ? regionsArray.filter(r => r.inputRegionId === id) : [];
    }
  };

  IMAGE_SELECTED ({state}) {
    // TODO: If image state is lazy loaded, why do we need to listen to this event?
    return this.getters.currentState;
  }

  // Listen for uScope annotations events and update state accordingly
  AFTER_VIEWPORT_CREATED ({state, get, dispatch}, viewport) {
    const imageId = viewport.rawImageId;
    // When a region is selected, update the selected region
    viewport.events.on('us:annotations:region-selected', (region) => {
      region = translateRegion(region);
      get('ANNOTATIONS').getState({imageId}).selectedRegionId = region ? region.id : null;
    });

    // When a layer is selected, update the selected layer
    viewport.events.on('us:annotations:layer-selected', (layer) => {
      layer = translateLayer(layer);
      get('ANNOTATIONS').getState({imageId}).selectedLayerId = layer ? layer.id : null;
    });

    // When a region is initially created it has a temporary Id. This gets updated after save, causing problems.
    // This particular event (region-change) is not one we want to keep around (and updating IDs is a bad practice
    // anyway), so we don't promote it in the store ecosystem. Eventually the store should manage annotation state
    // entirely and this hack should be unnecessary.
    viewport.events.on('us:annotations:region-change', ({action, region}) => {
      if (action !== 'add') {
        return;
      }
      dispatchRegionEvent('REGION_ADDED', region);
    });
    // ...and ditto for layers
    viewport.events.on('us:annotations:layer-saved', ({oldLayerId, layer}) => {
      layer = translateLayer(layer);
      dispatch('LAYER_ADDED', {layer});
    });

    // Relay uScope's "region" events
    viewport.events.on('us:annotations:region-added', dispatchRegionEvent.bind(null, 'REGION_ADDED'));
    viewport.events.on('us:annotations:region-removed', dispatchRegionEvent.bind(null, 'REGION_REMOVED'));
    viewport.events.on('us:annotations:region-deleted', dispatchRegionEvent.bind(null, 'REGION_DELETED'));

    viewport.events.on('us:annotations:layer-reinitialized', ({imageId, layer, regions}) => {
      const stateRef = this.getters.getState({imageId});
      const allRegions = stateRef.allRegions;
      layer = stateRef.allLayers[layer.get('Id')];

      forEach(regions, (region, oldRegionId) => {
        region = translateRegion(region);

        // Update the allRegions tracker
        delete allRegions[oldRegionId];
        allRegions[region.id] = region;

        // Update the layer's region data
        const ix = findIndex(layer.regions, {id: Number(oldRegionId)});
        if (ix > -1) {
          layer.regions.splice(ix, 1, region);
        } else {
          logger.warn('updated region that doesnt exist on layer', region);
        }
        dispatch('REGION_UPDATED', {imageId, oldRegionId, region});
      });

      dispatch('LAYER_REINITIALIZED');
    });

    // Relay uScope's "layer" events
    viewport.events.on('us:annotations:layer-added', (layer) => {
      layer = translateLayer(layer);
      if (layer.id > 0) {
        dispatch('LAYER_ADDED', {layer});
      }
    });
    viewport.events.on('us:annotations:layer-removed', (layerId) => {
      const layer = this.getters.getState({imageId}).allLayers[layerId];
      if (layer) {
        dispatch('LAYER_REMOVED', {layer});
      } else {
        logger.warn('us:annotations:layer-removed: could not locate layer', layerId);
      }
    });
    viewport.events.on('us:annotations:layers-loaded', () => {
      this.getters.getState({imageId}).areLayersLoaded = true;
      dispatch('LAYERS_LOADED');
    });
    viewport.events.on('$destroy', () => {
      get('ANNOTATIONS').getState({imageId}).areLayersLoaded = false;
    });

    function dispatchRegionEvent (event, region) {
      region = translateRegion(region);
      if (region.id > 0) {
        dispatch(event, {region});
      }
    }
  }

  /*
  * Region Management
  */

  // Event handler when a region is added to the viewport
  REGION_ADDED (store, {region}) {
    const stateRef = this.getters.getState(region);
    if (!stateRef) {
      throw new Error(`Added region for unknown image Id "${region.imageId}"`);
    }

    const regionCache = stateRef.allRegions;
    // Note that re-opening a viewport causes annotations to be reloaded. Side effects _should_ be nil...
    regionCache[region.id] = region;

    // Since the legacy system doesn't map regions to layers we have to do it manually.
    const layer = this.getters.getLayer(region);

    if (!layer) {
      throw new Error('Syncd a region without an existing layer. This should not be possible.');
    }

    const ix = findIndex(layer.regions, {id: region.id});
    if (ix > -1) {
      layer.regions.splice(ix, 1, region);
    } else {
      // TODO: these should be regionIds; Vue will copy the object rather than reference it
      layer.regions.push(region);
    }
  }

  // Command to select a region
  SELECT_REGION ({get}, {region, reposition = true}) {
    const annotationsService = get('VIEWPORT').plugins('us:annotations:service');
    const allRegions = [this.getters.currentState.allRegions];

    // Ensure region id is set
    allRegions.forEach(x => { Object.keys(x).forEach(key => { if (x[key].displayId === region.displayId && x[key].layerId === region.layerId) region.id = x[key].id; }); });

    annotationsService && annotationsService.regSelect({
      regionId: region.id,
      bReposition: reposition
    });
  }

  // Command to select the previous or next region
  MOVE_REGION_CURSOR ({get}, direction) {
    if (!CURSOR_OPTIONS.includes(direction)) {
      logger.warn(`MOVE_REGION_CURSOR: direction "${direction} is not supported`);
      return;
    }

    const annotationsService = get('VIEWPORT').plugins('us:annotations:service');
    annotationsService && annotationsService.regSelectNextPrev(direction);
    annotationsService.regHoverShow(false); // hide any hovering note
  }

  // Command to delete a region
  async DELETE_REGION ({dispatch, get}, {region}) {
    const annotationsService = get('VIEWPORT').plugins('us:annotations:service');
    const allRegions = [this.getters.currentState.allRegions];

    // Ensure region id is set
    allRegions.forEach(x => { Object.keys(x).forEach(key => { if (x[key].displayId === region.displayId && x[key].layerId === region.layerId) region.id = x[key].id; }); });

    return annotationsService.regDelete(region.id);
  }

  // Shorthand command to delete the selected region
  DELETE_SELECTED_REGION ({dispatch}) {
    dispatch('DELETE_REGION', {region: this.getters.selectedRegion});
  }

  // Event handler for region removed
  REGION_REMOVED (s, {region}) {
    // Remove the region from the allRegions list
    delete this.getters.getState(region).allRegions[region.id];

    // Remove the region from its layer's region list
    const layer = this.getters.getLayer(region);
    if (layer.regions) {
      pull(layer.regions, region);
    } else {
      logger.warn('Region layer reference doesn\'t have a region list');
    }
  }

  /*
  * Layer Management
  */

  // Event handler triggered when layer is added to the viewport
  LAYER_ADDED (store, {layer}) {
    const stateRef = this.getters.getState(layer);
    if (!stateRef) {
      throw new Error(`Added layer for unknown image Id "${layer.imageId}"`);
    }
    const layerCache = stateRef.allLayers;

    // Note that re-opening a viewport causes annotations to be reloaded, so this may overwrite. TODO: Evaluate side effects.
    layerCache[layer.id] = layer;
  }

  // Event handler triggered when layer is removed
  LAYER_REMOVED ({dispatch}, {layer}) {
    const state = this.getters.getState(layer);
    const allRegions = state.allRegions;
    if (layer.regions) {
      layer.regions.forEach(region => {
        delete allRegions[region.id];
      });
    }
    delete state.allLayers[layer.id];
  }

  // Command to delete one or more layers
  async DELETE_LAYERS ({state, resources, get, dispatch}, {layers}) {
    // Remove layers from the view first (don't wait on DB response so user sees immediate response)
    const ids = layers.map(l => l.id);
    const annotationsService = get('VIEWPORT').plugins('us:annotations:service');

    ids.forEach(id => {
      annotationsService.layers.removeLayer(id);
      // TODO: If non-IA services use this command, dispatch('LAYER_DELETED', {id}) and handle in IaResultsStore
      // For now this is unnecessary because IA_DELETE_CURRENT_RESULTS keeps track, and I can't think of a clean
      // way to decouple the "ensureMacroLayersExist" from the check to see if deleted layer was an output layer
      // that would be necessary.
    });

    // Now remove them from the DB
    const {selectedImageId} = state('IMAGE');
    const annotationsProvider = resources.get('ANNOTATIONS');
    await annotationsProvider.deleteLayers(selectedImageId, ids);
  }
}

function createState () {
  return makeObservable({
    allLayers: {},
    allRegions: {},
    selectedLayerId: null,
    selectedRegionId: null,
    areLayersLoaded: false
  });
}



// WEBPACK FOOTER //
// ./src/shared/stores/annotations/AnnotationsStore.js